import EditOutlined from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { BillingLineSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useContext, useState } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Invoice } from '~hooks/useInvoices/models';
import { JobInvoicing } from '~hooks/useJob/models';
import useJobInvoicesQuery, { jobInvoicesQueryKey } from '~queries/useJobInvoicesQuery';
import { useStores } from '~store';
import { formatRateWithType } from '~utils/rateFormatting';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import { NO_VALUE_PLACEHOLDER } from '../chargesGrid';
import { JobCharges } from '../groupBillingLines';
import { getInvoiceRateCardName } from '../invoiceRateCard';
import ChargesInvoiceEditDialog from './ChargesInvoiceEditDialog';

const SIDES = [BillingLineSide.PAYABLE, BillingLineSide.RECEIVABLE];

const sideLabelKeys = {
  [BillingLineSide.PAYABLE]: 'approvals.approve_charges.section_header.pay_rate_card',
  [BillingLineSide.RECEIVABLE]: 'approvals.approve_charges.section_header.rec_rate_card',
} satisfies Record<BillingLineSide, string>;

const editLabelKeys = {
  [BillingLineSide.PAYABLE]: 'approvals.approve_charges.actions.edit_payable',
  [BillingLineSide.RECEIVABLE]: 'approvals.approve_charges.actions.edit_receivable',
} satisfies Record<BillingLineSide, string>;

interface ChargesSectionHeaderProps {
  charges?: JobCharges;
  job: JobInvoicing;
  readOnlySides?: BillingLineSide[];
}

export default function ChargesSectionHeader({
  charges,
  job,
  readOnlySides = [],
}: ChargesSectionHeaderProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { currencyFormatter } = useCompanyCurrency();
  const { reload } = useContext(ChargesBillingLinesQueryContext);
  const queryClient = useQueryClient();
  const [editedSide, setEditedSide] = useState<BillingLineSide | null>(null);
  const { isLoading, payable, receivable } = useJobInvoicesQuery(companyId, job.id);
  const invoiceBySide = {
    [BillingLineSide.PAYABLE]: payable,
    [BillingLineSide.RECEIVABLE]: receivable,
  } satisfies Record<BillingLineSide, Invoice | null>;

  const rateCardText = (invoice: Invoice | null) => {
    if (!invoice) {
      return NO_VALUE_PLACEHOLDER;
    }

    const rate = formatRateWithType(
      invoice.rateValue,
      invoice.rateType,
      currencyFormatter,
      {
        unitStyle: 'short',
      },
    );

    return (
      [getInvoiceRateCardName(invoice), rate].filter(Boolean).join(' · ') ||
      NO_VALUE_PLACEHOLDER
    );
  };

  const editableSides = SIDES.filter(
    (side) => charges?.[side].hasLines && !readOnlySides.includes(side),
  );

  return (
    <Box
      data-test-id="charges-section-header"
      sx={(theme) => ({
        alignItems: 'center',
        borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        display: 'flex',
        gap: 2,
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, lineHeight: '20px' }}>
          {t('approvals.approve_charges.section_header.title')}
        </Typography>
        {isLoading ? (
          <Skeleton sx={{ width: 240 }} variant="text" />
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {SIDES.map((side) => (
              <Typography
                key={side}
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadGray2,
                  fontSize: '12px',
                  lineHeight: '16px',
                })}
              >
                {t(sideLabelKeys[side], {
                  interpolation: { escapeValue: false },
                  rateCard: rateCardText(invoiceBySide[side]),
                })}
              </Typography>
            ))}
          </Box>
        )}
        {job.internalNotes && (
          <Typography
            data-test-id="charges-section-internal-notes"
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray2,
              fontSize: '12px',
              lineHeight: '16px',
              mt: 0.5,
              whiteSpace: 'pre-line',
            })}
          >
            {t('approvals.approve_charges.section_header.internal_notes', {
              interpolation: { escapeValue: false },
              notes: job.internalNotes,
            })}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', flexShrink: 0, gap: 1 }}>
          {editableSides.map((side) => (
            <SmallButton
              key={side}
              onClick={() => setEditedSide(side)}
              startIcon={<EditOutlined />}
              sx={{ borderColor: 'divider', color: 'text.primary' }}
              variant="outlined"
            >
              {t(editLabelKeys[side])}
            </SmallButton>
          ))}
        </Box>
        {editableSides.length > 0 && (
          <Typography
            sx={(theme) => ({ color: theme.brandV2.colors.treadGray3, fontSize: '11px' })}
          >
            {t('approvals.approve_charges.section_header.edit_rates_placeholder')}
          </Typography>
        )}
      </Box>

      {editedSide && (
        <ChargesInvoiceEditDialog
          jobId={job.id}
          onClose={() => {
            setEditedSide(null);
            void queryClient.invalidateQueries({
              queryKey: jobInvoicesQueryKey(companyId, job.id),
            });
            reload();
          }}
          side={editedSide}
        />
      )}
    </Box>
  );
}
